import { range } from "../../../utils";
import {
  getPhotoUrl,
  type PhotoBookOptions,
  type ProductTypeConfig,
  type Asset,
  type PhotoRecommendation,
  type PhotoValue,
} from "../../types";
import layouts from "./layout/layouts";
import { ProductEdit, ProductPreview, ProductThumbnail } from "./product";
import ProductSettings from "./settings";

interface PromoCodes {
  [key: string]: number;
}

interface Prices {
  album: number;
  page: number;
  promoCodes: PromoCodes;
}

const prices: Prices = {
  album: 2900,
  page: 250,
  promoCodes: {
    FAMILYONE: 35,
    LYMALOVE: 20,
  },
};

const config: ProductTypeConfig<PhotoBookOptions> = {
  name: "photo-book",
  title: "Livre Photo Classique",
  labels: {
    pendingTitle: "Livres en cours de création",
    pendingMessage:
      "Vous avez des livres photos en cours de création, voulez vous reprendre leur modification ou créer un nouveau?",
    newButton: "Créer un nouveau livre photo",
  },

  options: {
    countPages: {
      defaultValue: 20,
    },
    pages: {
      defaultValue: {},
    },
    coverPhoto: {
      defaultValue: null,
    },
    theme: {
      defaultValue: null,
    },
    color: {
      defaultValue: null,
    },
    title: {
      defaultValue: null,
    },
  },

  price(options: PhotoBookOptions, code?: string, shippingPrice: number = 0): number {
    const price = prices.album + Math.max(0, options.countPages - 20) * prices.page;
    const discount = code && prices.promoCodes[code] ? prices.promoCodes[code] : 0;

    return price - (price * discount) / 100 + shippingPrice;
  },

  priceRows(
    options: PhotoBookOptions,
    code?: string,
    shippingPrice: number = 0,
  ): Array<[string, string]> {
    const steps: Array<[string, string]> = [
      ["Livre photo", prices.album + " DA"],
      [
        "Pages Supp.",
        `${Math.max(0, options.countPages - 20)} x ${prices.page} = ${
          Math.max(0, options.countPages - 20) * prices.page
        } DA`,
      ],
    ];

    const discount = code && prices.promoCodes[code] ? prices.promoCodes[code] : 0;

    if (discount) {
      steps.push(["Code Promo.", "-" + discount + "%"]);
    }

    steps.push(["Livraison", `${shippingPrice}  DA`]);

    return steps;
  },

  isReady(options: PhotoBookOptions): boolean {
    return range(options.countPages).every((pageIndex) => {
      const page = options.pages[pageIndex];
      if (!page) {
        return false;
      }

      const selectedLayout = layouts.find((layout) => layout.value === (page.layout || "margin"));

      return (
        !!selectedLayout &&
        !!page.photos &&
        range(selectedLayout.countPhotos).every((index) => !!page.photos?.[index])
      );
    });
  },

  photoRecommendation(options: PhotoBookOptions): PhotoRecommendation {
    const pageCount = options.countPages || 20;
    const minPhotos = pageCount;
    const recommendedPhotos = pageCount * 2;
    return {
      min: minPhotos,
      recommended: recommendedPhotos,
      message: `Nous recommandons ${minPhotos}-${recommendedPhotos} photos pour un livre de ${pageCount} pages`,
    };
  },

  getAssets(options: PhotoBookOptions): Asset[] {
    const photos: Asset[] = [];
    if (options.coverPhoto) {
      photos.push({
        title: "Couverture",
        url: getPhotoUrl(options.coverPhoto),
        filename: "cover",
      });
    }

    const addPhotos = (
      page: { photos?: Record<number, PhotoValue> },
      index: string,
      keys: number[],
    ) => {
      keys.forEach((key) => {
        if (page.photos && page.photos[key]) {
          photos.push({
            title: `Photo page ${parseInt(index, 10) + 1} - ${key + 1}`,
            url: getPhotoUrl(page.photos[key]),
            filename: `page-${parseInt(index, 10) + 1}-${key + 1}`,
          });
        }
      });
    };

    Object.entries(options.pages || {}).forEach(([index, page]) => {
      const selectedLayout = layouts.find((layout) => layout.value === (page.layout || "margin"));

      if (selectedLayout) {
        addPhotos(page, index, range(selectedLayout.countPhotos));
      }
    });

    return photos;
  },

  settings: ProductSettings,

  edit: ProductEdit,

  preview: ProductPreview,

  thumbnail: ProductThumbnail,
};

export default config;
